import {
  AgendaSlotFilterStatus,
  AgendaSlotReservationApi,
  agendaRruleSchema,
  agendaSlotTypeSchema,
  BUSINESS_TIMEZONE,
  periodsOfTheDay,
  pictureSchema,
  reservationStatus,
  SlotReservationDoctorSubstitute,
  UnauthorizedError,
} from '@omecare/utils';
import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import http from '@/utils/http.service';

export const agendaSlotReservationSchema = z.object({
  id: z.string().uuid(),
  status: z.enum(reservationStatus),
  tag: z.string().optional(),
  doctorSubstitute: z.object({
    id: z.string().uuid(),
    firstname: z.string(),
    lastname: z.string(),
    picture: pictureSchema,
    phoneNumber: z.string().optional(),
    email: z.string().optional(),
    rhythm: z.string().optional(),
    onSiteSlotsWithoutAssistant: z.number().nullable().optional(),
    remoteSlotsWithoutAssistant: z.number().nullable().optional(),
    onSiteSlotsWithAssistant: z.number().nullable().optional(),
    remoteSlotsWithAssistant: z.number().nullable().optional(),
    rhythmNote: z.string().nullable().optional(),
    priority: z.number().nullable().optional(),
    installable: z.boolean().default(false),
    hasPhysicalSiteAccess: z.boolean().default(false),
  }),
});
export type AgendaSlotReservation = z.infer<typeof agendaSlotReservationSchema>;

export const getAgendaSlotItemSchema = z.object({
  id: z.string().uuid(),
  startAt: z.coerce.date(),
  endAt: z.coerce.date(),
  periodOfTheDay: z.enum(periodsOfTheDay),
  type: agendaSlotTypeSchema,
  doctorAttending: z.object({
    id: z.string().uuid(),
    firstname: z.string(),
    lastname: z.string(),
  }),
  rrule: agendaRruleSchema,
  exdate: z.array(z.string()),
  retrocessionRate: z.number(),
  withAssistant: z.boolean(),
  withAssistantInferred: z.boolean().default(false),
  forceWithoutAssistant: z.coerce.boolean().optional().default(false),
  sourceId: z.string().uuid().nullable(),
  occurenceIndex: z.number().optional(),
  morningDoctolibDataCreatedAt: z.coerce.date().nullable().default(null),
  isFeeApplicable: z.coerce.boolean().nullable(),
  workAtTheSameTime: z
    .object({
      morning: z.coerce.number(),
      afternoon: z.coerce.number(),
      // Slots frozen before the night period existed have no night counter.
      night: z.coerce.number().optional().default(0),
    })
    .nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  medicalAssistantWorkDuration: z.number().optional(),
  medicalAssistantWorkPeriods: z
    .array(
      z.object({
        startTime: z.coerce.date(),
        endTime: z.coerce.date(),
        date: z.coerce.date(),
      }),
    )
    .optional(),
  // Computed assistant periods (day-boundary extended, pauses removed, Formation
  // excluded), resolved server-side. Preferred over the legacy Skello periods.
  assistantPeriods: z
    .array(
      z.object({
        assistantId: z.string(),
        userId: z.string(),
        firstname: z.string(),
        lastname: z.string(),
        startTime: z.string(),
        endTime: z.string(),
        durationMinutes: z.number(),
      }),
    )
    .optional()
    .default([]),
  reconciliationWarningType: z.string().nullable().optional(),
  pendingRequestCount: z.number().default(0),
  availableSubstitutesCount: z.number().default(0),
  pendingInvitation: z
    .object({
      firstName: z.string(),
      lastName: z.string(),
      email: z.string(),
    })
    .nullable()
    .default(null),
  reservations: z.array(agendaSlotReservationSchema).optional().default([]),
});
export type GetAgendaSlotItemResponse = z.infer<typeof getAgendaSlotItemSchema>;
export type AgendaSlotItemReservation =
  GetAgendaSlotItemResponse['reservations'][number];

export const getAgendaSlotsSchema = z.object({
  data: z.array(getAgendaSlotItemSchema),
});

export type GetAgendaSlotsResponse = z.infer<typeof getAgendaSlotsSchema>;

export type GetOfficeAgendaSlotsParams = {
  officeId: string;
  status: AgendaSlotFilterStatus[];
  from: Date;
  to: Date;
};
/** Root of every office agenda-slots key, for range-wide invalidation. */
export const OFFICE_AGENDA_SLOTS_QUERY_ROOT = 'office-agenda-slots';

export const getOfficeAgendaSlotsQueryKey = (
  params: GetOfficeAgendaSlotsParams,
) => [
  OFFICE_AGENDA_SLOTS_QUERY_ROOT,
  params.officeId,
  params.status.toString(),
  params.from?.toISOString(),
  params.to?.toISOString(),
];

export const getOfficeAgendaSlots =
  (params: GetOfficeAgendaSlotsParams) => async () => {
    if (params.status.length === 0) {
      return {
        data: [],
      };
    }

    return http.agendaSlots
      .getList({
        ...params,
        timezone: BUSINESS_TIMEZONE,
      })
      .then<GetAgendaSlotsResponse>(({ response, transform }) => {
        const result = transform.handle({
          response,
          attributes: {
            startAt: true,
            endAt: true,
            periodOfTheDay: true,
            type: true,
            rrule: true,
            exdate: true,
            retrocessionRate: true,
            withAssistant: true,
            withAssistantInferred: true,
            forceWithoutAssistant: true,
            sourceId: true,
            occurenceIndex: true,
            morningDoctolibDataCreatedAt: true,
            isFeeApplicable: true,
            workAtTheSameTime: true,
            createdAt: true,
            updatedAt: true,
            medicalAssistantWorkDuration: true,
            medicalAssistantWorkPeriods: true,
            assistantPeriods: true,
            reconciliationWarningType: true,
            pendingRequestCount: true,
            availableSubstitutesCount: true,
            pendingInvitation: true,
          },
          relations: {
            doctorAttending: true,
            reservations: ({
              entity,
            }: {
              entity: AgendaSlotReservationApi[];
            }): AgendaSlotItemReservation[] => {
              return entity
                .map((res) => {
                  const doctorSubstitute =
                    transform.getApiEntityRelation<SlotReservationDoctorSubstitute>(
                      {
                        relation: 'doctorSubstitute',
                        relationships: res.relationships,
                        included: response.included,
                      },
                    );
                  if (!doctorSubstitute) {
                    return null;
                  }
                  return {
                    id: res.id,
                    status: res.attributes.status,
                    tag: res.attributes.tag,
                    doctorSubstitute: {
                      id: doctorSubstitute.id,
                      firstname: doctorSubstitute.attributes.firstname,
                      lastname: doctorSubstitute.attributes.lastname,
                      picture: doctorSubstitute.attributes.picture ?? null,
                      phoneNumber: doctorSubstitute.attributes.phoneNumber,
                      email: doctorSubstitute.attributes.email,
                      rhythm: doctorSubstitute.attributes.rhythm,
                      onSiteSlotsWithoutAssistant:
                        doctorSubstitute.attributes
                          .onSiteSlotsWithoutAssistant ?? null,
                      remoteSlotsWithoutAssistant:
                        doctorSubstitute.attributes
                          .remoteSlotsWithoutAssistant ?? null,
                      onSiteSlotsWithAssistant:
                        doctorSubstitute.attributes.onSiteSlotsWithAssistant ??
                        null,
                      remoteSlotsWithAssistant:
                        doctorSubstitute.attributes.remoteSlotsWithAssistant ??
                        null,
                      rhythmNote:
                        doctorSubstitute.attributes.rhythmNote ?? null,
                      priority: doctorSubstitute.attributes.priority ?? null,
                      installable: doctorSubstitute.attributes.installable,
                      hasPhysicalSiteAccess:
                        doctorSubstitute.attributes.hasPhysicalSiteAccess ??
                        false,
                    },
                  };
                })
                .filter((r): r is NonNullable<typeof r> => r !== null);
            },
          },
          schema: getAgendaSlotsSchema,
        });

        return {
          data: result.data as GetAgendaSlotsResponse['data'],
        };
      });
  };

export const useGetOfficeAgendaSlotsQuery = (
  params: GetOfficeAgendaSlotsParams,
  enabled: boolean,
): UseQueryResult<GetAgendaSlotsResponse, UnauthorizedError> => {
  return useQuery({
    queryFn: getOfficeAgendaSlots(params),
    queryKey: getOfficeAgendaSlotsQueryKey(params),
    retry: false,
    enabled,
  });
};
